import React, { useCallback } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { DialogActions, DialogContent, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
  WBButton,
  WBCheckbox,
  WBFlex,
  WBForm,
  WBIconButton,
  WBLinkButton,
  WBTextFieldV2,
  WBTypography,
} from '@admiin-com/ds-web';
import { useCurrentEntityId } from '../../../hooks/useSelectedEntity/useSelectedEntity';
import SignerLookup from '../../AutoCompleteLookup/SignerLookup';
import { REGEX } from '@admiin-com/ds-common';
import { Signer } from '../../../features/providers/TaskCreationProvider/TaskCreationProvider';
import { useTaskCreationContext } from '../../../pages/TaskCreation';
// Types for props and form data
interface RecipientAddFormProps {
  onSubmit: (data: Signer[]) => void;
  onClose: () => void;
  signers?: Signer[];
}

// Type definition for a single signer field
interface SignerField {
  id?: string;
  name: string;
  email: string;
  signerType: string;
  secondName?: string;
  entityId?: string;
  noLookup?: boolean;
  mobile?: string;
  createContact?: boolean;
  initialPages?: boolean;
}

const MIN_PHONE_DIGITS = 6;

const NameField = React.memo(
  ({
    index,
    entityId,
    noGroupBy,
  }: {
    index: number;
    entityId: string;
    noGroupBy: boolean;
  }) => {
    const { t } = useTranslation();

    const {
      control,
      setValue,
      formState: { errors },
    } = useFormContext<{ signers: SignerField[] }>();

    return (
      <Controller
        control={control}
        name={`signers.${index}`}
        rules={{
          validate: (value: any) => {
            if (!value || !value.name) {
              return t('signerRequired', { ns: 'taskbox' });
            }
            return true;
          },
        }}
        render={({ field }) => (
          <SignerLookup
            {...field}
            label={t('name', { ns: 'taskbox' })}
            placeholder={t('selectName', { ns: 'taskbox' })}
            type={'Signer'}
            entityId={entityId}
            noLookup={field.value?.noLookup}
            noGroupBy={noGroupBy}
            error={!!errors?.signers?.[index]}
            tabIndex={0}
            helperText={(errors?.signers?.[index]?.message as string) || ''}
            onChange={(data) => {
              const newData: SignerField = {
                ...field.value,
                noLookup: data.noLookup ?? field.value?.noLookup,
              };
              if (data.id) {
                newData.id =
                  data.searchType === 'CONTACT' ? data.id : data.userId;
                newData.name = data.name;
                newData.email = data.email ?? '';
                newData.signerType = data.searchType;
                newData.secondName = data.secondName;
                newData.mobile = data.mobile ?? data.phone ?? '';
                newData.createContact = false;
              } else {
                newData.id = uuidv4();
                newData.name = data.name ?? '';
                newData.email = data.email ?? '';
                newData.signerType = 'GUEST';
                newData.mobile = data.mobile ?? '';
                newData.createContact = true;
              }
              newData.entityId = data?.entityId ?? entityId;

              setValue(`signers.${index}`, newData, {
                shouldValidate: false,
                shouldDirty: true,
              });
            }}
          />
        )}
      />
    );
  }
);

const EmailField = React.memo(
  ({
    index,
    control,
    errors,
  }: {
    index: number;
    control: any;
    errors: any;
  }) => {
    const { t } = useTranslation();
    const value = useWatch({ control, name: `signers.${index}.email` });
    return (
      <Controller
        control={control}
        name={`signers.${index}.email`}
        rules={{
          required: t('emailRequired', { ns: 'common' }),
          pattern: {
            value: REGEX.EMAIL,
            message: t('invalidEmail', { ns: 'common' }),
          },
        }}
        render={({ field }) => (
          <WBTextFieldV2
            {...field}
            value={value}
            label={`${t('email', { ns: 'contacts' })}*`}
            placeholder={t('emailPlaceholder', { ns: 'contacts' })}
            error={!!errors?.signers?.[index]?.email}
            helperText={errors?.signers?.[index]?.email?.message || ''}
          />
        )}
      />
    );
  }
);

// const MobileField = React.memo(
//   ({
//     index,
//     control,
//     errors,
//   }: {
//     index: number;
//     control: any;
//     errors: any;
//   }) => {
//     const { t } = useTranslation();
//     const value = useWatch({ control, name: `signers.${index}.mobile` });
//
//     return (
//       <Controller
//         control={control}
//         name={`signers.${index}.mobile`}
//         rules={{
//           validate: (mobile?: string) => {
//             if (!mobile) return true;
//             const numeric = mobile.replace(/\D/g, '');
//             if (numeric.length < MIN_PHONE_DIGITS) {
//               return t('invalidPhone', { ns: 'common' });
//             }
//             return true;
//           },
//         }}
//         render={({ field }) => (
//           <WBTextFieldV2
//             {...field}
//             value={value ?? ''}
//             label={t('mobile', { ns: 'contacts' })}
//             placeholder={t('mobilePlaceholder', { ns: 'contacts' })}
//             error={!!errors?.signers?.[index]?.mobile}
//             helperText={errors?.signers?.[index]?.mobile?.message || ''}
//           />
//         )}
//       />
//     );
//   }
// );

// const CreateContactCheckbox = React.memo(({ index }: { index: number }) => {
//   const { t } = useTranslation();
//   const { control } = useFormContext<{ signers: SignerField[] }>();
//   const signerType = useWatch({ control, name: `signers.${index}.signerType` });
//   const noLookup = useWatch({ control, name: `signers.${index}.noLookup` });
//
//   const shouldShow = signerType === 'GUEST' || noLookup;
//
//   if (!shouldShow) return null;
//
//   return (
//     <Controller
//       control={control}
//       name={`signers.${index}.createContact`}
//       render={({ field }) => (
//         <WBCheckbox
//           {...field}
//           checked={field.value ?? true}
//           onChange={(event) => field.onChange(event.target.checked)}
//           label={t('saveRecipientAsContact', { ns: 'taskbox' })}
//         />
//       )}
//     />
//   );
// });

export const RecipientAddForm: React.FC<RecipientAddFormProps> = (props) => {
  const currentEntityId = useCurrentEntityId();
  const { client, isTaxBinding, taskType } = useTaskCreationContext();
  const { control: formControl } = useFormContext();
  const to = useWatch({ control: formControl, name: 'to' });
  const assignedEntityId = to?.entityId ?? to?.id;
  const entityId =
    (isTaxBinding ? assignedEntityId : null) ?? client?.id ?? currentEntityId;

  const noGroupBy = taskType === 'tax';

  const theme = useTheme();
  const methods = useForm<{ signers: SignerField[] }>({
    values: {
      signers:
        props.signers?.map((signer) => ({
          ...signer,
          createContact:
            signer.createContact !== undefined
              ? signer.createContact
              : signer.signerType === 'GUEST',
        })) ?? [],
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'signers',
  });
  const { t } = useTranslation();

  const onSubmit = useCallback(
    (data: { signers: SignerField[] }) => {
      props.onSubmit(data.signers as any);
    },
    [props]
  );

  const handleAdd = () => {
    if (fields.length < 5)
      append({
        email: '',
        name: '',
        signerType: 'GUEST',
        id: uuidv4(),
        mobile: '',
        createContact: true,
        initialPages: false,
      });
  };

  React.useEffect(() => {
    if (fields.length === 0) {
      handleAdd();
    }
  }, [fields]);

  return (
    <FormProvider {...methods}>
      <WBForm onSubmit={handleSubmit(onSubmit)} mt={0}>
        <DialogContent sx={{ minHeight: '300px', px: 0, py: 0, zIndex: 1000 }}>
          {fields.length === 0 ? (
            <WBTypography mb={2} px={3}>
              {t('createRecepientDescription', { ns: 'taskbox' })}
            </WBTypography>
          ) : (
            fields.map((field, index) => (
              <React.Fragment key={field.id}>
                <WBFlex
                  sx={{ display: ['none', 'flex'] }}
                  flexDirection="column"
                  gap={1}
                >
                  <WBFlex alignItems={'flex-start'} gap={3}>
                    <WBTypography>{index + 1}</WBTypography>
                    <WBFlex flex={1} minWidth={0}>
                      <NameField
                        index={index}
                        entityId={entityId}
                        noGroupBy={noGroupBy}
                      />
                    </WBFlex>
                    <WBFlex flex={1} minWidth={0}>
                      <EmailField
                        index={index}
                        control={control}
                        errors={errors}
                      />
                    </WBFlex>
                    {/*<WBFlex flex={1} minWidth={0}>*/}
                    {/*  <MobileField*/}
                    {/*    index={index}*/}
                    {/*    control={control}*/}
                    {/*    errors={errors}*/}
                    {/*  />*/}
                    {/*</WBFlex>*/}
                    <WBIconButton
                      icon="Close"
                      color={theme.palette.grey[500] as any}
                      onClick={() => remove(index)}
                    />
                  </WBFlex>
                  <WBFlex pl={'44px'} gap={2}>
                    {/*<CreateContactCheckbox index={index} />*/}
                    {/*<InitialPagesCheckbox index={index} />*/}
                  </WBFlex>
                </WBFlex>
                <WBFlex
                  my={1}
                  sx={{
                    bgcolor: 'background.paper',
                    display: ['flex', 'none'],
                  }}
                  gap={1}
                  width={'100%'}
                  alignItems={'start'}
                  justifyContent={'space-between'}
                >
                  <WBFlex
                    flex={1}
                    p={2}
                    mx={2}
                    flexDirection={'column'}
                    alignItems={'center'}
                  >
                    <NameField
                      index={index}
                      entityId={entityId}
                      noGroupBy={noGroupBy}
                    />
                    <EmailField
                      index={index}
                      control={control}
                      errors={errors}
                    />
                    {/*<MobileField*/}
                    {/*  index={index}*/}
                    {/*  control={control}*/}
                    {/*  errors={errors}*/}
                    {/*/>*/}
                    {/*<CreateContactCheckbox index={index} />*/}
                    {/*<InitialPagesCheckbox index={index} />*/}
                    <WBIconButton
                      icon="Close"
                      color={theme.palette.grey[500] as any}
                      onClick={() => remove(index)}
                    />
                  </WBFlex>
                </WBFlex>
              </React.Fragment>
            ))
          )}
          {fields.length <= 5 && (
            <WBFlex justifyContent={'end'} mt={3} color={'primary.main'}>
              <WBLinkButton onClick={() => handleAdd()}>
                {t('addRecipient', { ns: 'taskbox' })}
              </WBLinkButton>
            </WBFlex>
          )}
        </DialogContent>

        <DialogActions>
          <WBButton
            type="button"
            variant="outlined"
            sx={{ px: 4 }}
            onClick={props.onClose}
          >
            {t('cancel', { ns: 'taskbox' })}
          </WBButton>
          <WBButton sx={{ px: 4 }} type="submit">
            {t('done', { ns: 'taskbox' })}
          </WBButton>
        </DialogActions>
      </WBForm>
    </FormProvider>
  );
};
